/*************************************************************************
 * ADOBE CONFIDENTIAL
 * ___________________
 *
 *  Copyright 2018 Adobe
 *  All Rights Reserved.
 *
 * NOTICE:  All information contained herein is, and remains
 * the property of Adobe and its suppliers, if any. The intellectual
 * and technical concepts contained herein are proprietary to Adobe
 * and its suppliers and are protected by all applicable intellectual
 * property laws, including trade secret and copyright laws.
 * Dissemination of this information or reproduction of this material
 * is strictly forbidden unless prior written permission is obtained
 * from Adobe.
 **************************************************************************/
/*
 * This function is written to handle multiple promises received from rendition provider
 * (ajs and dcapi) client can add callbacks for both promises in do function of this class.
 * functionality like choosing which promise to resolve when can be added here.
 */

import {
  getDocPermissions,
  isFilePasswordProtected,
  isDocumentEncrypted,
  getIsXFA,
  getPermittedOperations,
  getDisplayPermissionToast
} from "selectors/documentSelectors";
import { isReadOnlyModeEnabled, getIntl } from "selectors/appSelectors";
import { getShouldEnableEncryptedWrite } from "selectors/overlayDropinSelectors";
import * as stores from "stores";
import * as constants from "constants/constants";
import { subscribeToSelector } from "dc-preview-utils/src/Utils/MobXUtils";
import { getNoPreviewErrorCode } from "selectors/documentViewSelectors";
import {
  noPreviewErrorCodes,
  documentPropertiesMenuItems,
  docPropertiesDialogTriggerPoint,
  OPERATIONS_BIT_MAP,
  PERMISSION_LEVEL
} from "constants/enums";
import { isSharedFileOnMobile } from "utils/DocumentUtils";
import { dialogApi, dialogTypes } from "components/dialogs";
import { trackViewDocPermissionsToast, trackViewDocPropertiesDialog } from "analytics/events";
import PermissionDetailsCSS from "documentProperties/PermissionDetails.css";
import { shouldShowDocumentProperties } from "utils/AppUIUtils";

export const isAllowed = perm => {
  let docPerm = getDocPermissions(stores);
  return (docPerm >>> perm) & 1;
};

export const isOperationAllowed = (level, operation) => {
  switch (level) {
    case PERMISSION_LEVEL.ANNOT:
    case PERMISSION_LEVEL.DOC:
    case PERMISSION_LEVEL.FORM:
      return !!(getPermittedOperations(stores)[level] & operation);
    default:
      return true;
  }
};

export const isFormFillingAllowed = () => {
  const formFillingRestricted = isReadOnlyModeEnabled(stores) || isSharedFileOnMobile() || getIsXFA(stores);
  const isDocumentSecured = hasSecurityBitSet() || isFilePasswordProtected(stores) || isDocumentEncrypted(stores);
  if (formFillingRestricted || (!getShouldEnableEncryptedWrite(stores) && isDocumentSecured)) return false;

  return isOperationAllowed(PERMISSION_LEVEL.FORM, OPERATIONS_BIT_MAP.FILL_IN);
};

export const isCommentingAllowed = () => {
  const isDocumentSecured = hasSecurityBitSet() || isFilePasswordProtected(stores) || isDocumentEncrypted(stores);
  if (isReadOnlyModeEnabled(stores) || (!getShouldEnableEncryptedWrite(stores) && isDocumentSecured)) return false;

  return (
    isOperationAllowed(PERMISSION_LEVEL.ANNOT, OPERATIONS_BIT_MAP.CREATE) ||
    isOperationAllowed(PERMISSION_LEVEL.ANNOT, OPERATIONS_BIT_MAP.DELETE) ||
    isOperationAllowed(PERMISSION_LEVEL.ANNOT, OPERATIONS_BIT_MAP.MODIFY)
  );
};

export const isPrintingAllowed = () => isOperationAllowed(PERMISSION_LEVEL.DOC, OPERATIONS_BIT_MAP.PRINT);

export const hasSecurityBitSet = () => {
  let docPerm = getDocPermissions(stores);
  return (((docPerm | 3) + 1) & docPerm) !== 0;
};

export const isModifyingContentAllowed = () => isOperationAllowed(PERMISSION_LEVEL.DOC, OPERATIONS_BIT_MAP.MODIFY);

export const getDocumentRestrictions = () => {
  let copyingContentAllowed = isOperationAllowed(PERMISSION_LEVEL.DOC, OPERATIONS_BIT_MAP.COPY);
  let printAllowed = isOperationAllowed(PERMISSION_LEVEL.DOC, OPERATIONS_BIT_MAP.PRINT_LOW);
  let highQualityPrintingAllowed = isOperationAllowed(PERMISSION_LEVEL.DOC, OPERATIONS_BIT_MAP.PRINT_HIGH);
  let commentingAllowed = isCommentingAllowed();
  let formFillingAllowed = commentingAllowed || isFormFillingAllowed();
  let copyingContentForAccessibilityAllowed = isAllowed(constants.COPY_FOR_ACCESSIBILITY_PERM) === 1;
  let documentAssemblyAllowed = isAllowed(constants.DOCUMENT_ASSEMBLY_PERM) === 1;
  let modifyingContentAllowed = isModifyingContentAllowed();
  return {
    copyingContentAllowed,
    printingAllowed: printAllowed || highQualityPrintingAllowed,
    highQualityPrintingAllowed,
    commentingAllowed,
    formFillingAllowed,
    signingAllowed: formFillingAllowed,
    copyingContentForAccessibilityAllowed,
    pageExtractionAllowed: documentAssemblyAllowed,
    documentAssemblyAllowed,
    modifyingContentAllowed
  };
};

export const isCopyAllowed = () => {
  const docPermissions = getDocumentRestrictions();
  return docPermissions.copyingContentAllowed;
};

export const getDocumentSecurityInformation = () =>
  new Promise((resolve, reject) => {
    subscribeToSelector(stores, getDocPermissions, () => {
      if (getDocPermissions(stores) !== 0) {
        resolve({
          documentRestrictions: getDocumentRestrictions(),
          documentSecurity: isFilePasswordProtected(stores) ? "passwordSecurity" : "None"
        });
      }
    });

    subscribeToSelector(stores, getNoPreviewErrorCode, () => {
      if (getNoPreviewErrorCode(stores) !== noPreviewErrorCodes.NO_ERROR) {
        reject({ errorCode: getNoPreviewErrorCode(stores) });
      }
    });
  });

export const openDocPermissionDialog = (activeElem, triggerPoint) => {
  dialogApi.show(dialogTypes.DOCUMENT_PROPERTIES_DIALOG, {
    closeDialog: () => dialogApi.hide(),
    activeElem
  });
  trackViewDocPropertiesDialog(triggerPoint);
};

export const shouldShowPermissionsInfo = () => {
  const docRestrictions = getDocumentRestrictions();
  for (let key in docRestrictions) {
    if (!docRestrictions[key]) {
      return true;
    }
  }
  return false;
};

const _shouldShowPermissionsToast = () =>
  getDisplayPermissionToast(stores) &&
  shouldShowDocumentProperties() &&
  (isFilePasswordProtected(stores) || shouldShowPermissionsInfo());

export const showDocPermissionsToast = () => {
  const permToastConstants = constants.PERM_TOAST_CONSTANTS;
  // we need to display this toast for 5 seconds initially and then for 3 seconds the next two times.
  const numTimesToastAppeared = parseInt(localStorage.getItem(permToastConstants.LOCAL_STORAGE_KEY)) || 0;
  if (numTimesToastAppeared < permToastConstants.TOTAL_LIMIT && _shouldShowPermissionsToast()) {
    const intl = getIntl(stores);
    const timeout = numTimesToastAppeared === 0 ? permToastConstants.FIRST_TIMEOUT : permToastConstants.TIMEOUT;
    let messageId = "modernviewer.docPermissionsToast.message";
    let actionLabel = intl.formatMessage({ id: "modernviewer.docPermissionsToast.label" });
    if (!shouldShowPermissionsInfo() && isFilePasswordProtected(stores)) {
      messageId = "modernviewer.docPermissionsToast.passwordProtectedFile.message";
      actionLabel = null;
    }
    stores.notificationViewStore.addNotification(intl.formatMessage({ id: messageId }), {
      overrideClassName: PermissionDetailsCSS.toast,
      type: "toast",
      timeout,
      actionLabel,
      onAction: () => {
        openDocPermissionDialog(documentPropertiesMenuItems.PERMISSIONS, docPropertiesDialogTriggerPoint.TOAST);
      },
      onClose: () => {
        localStorage.setItem(permToastConstants.LOCAL_STORAGE_KEY, numTimesToastAppeared + 1);
      }
    });
    trackViewDocPermissionsToast();
  }
};
